import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import LeadService from "../services/LeadService";
import {
  getCategoryColor,
  LEAD_CATEGORY_COLORS,
  PAGE_BG,
  PURPLE,
  PURPLE_DARK,
} from "../components/leadConstants";

function apiErrorMessage(err, fallback) {
  return err?.response?.data?.error?.message
    || err?.response?.data?.message
    || err?.message
    || fallback;
}

const LeadCategoriesView = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [formName, setFormName] = useState("");
  const [formColor, setFormColor] = useState(LEAD_CATEGORY_COLORS[0]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await LeadService.getCategories();
      setCategories([...data].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    } catch {
      setSnackbar({ open: true, message: "Failed to load categories", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const openCreateDialog = () => {
    setEditingCategory(null);
    setFormName("");
    setFormColor(LEAD_CATEGORY_COLORS[0]);
    setDialogOpen(true);
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormName(category.name || "");
    setFormColor(getCategoryColor(category));
    setDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!formName.trim()) return;
    setSaving(true);
    try {
      const payload = { name: formName.trim(), color: formColor };
      if (editingCategory) {
        await LeadService.updateCategory(editingCategory.id, payload);
        setSnackbar({ open: true, message: "Category updated", severity: "success" });
      } else {
        await LeadService.createCategory(payload);
        setSnackbar({ open: true, message: "Category created", severity: "success" });
      }
      setDialogOpen(false);
      await loadCategories();
    } catch (err) {
      setSnackbar({
        open: true,
        message: apiErrorMessage(err, "Failed to save category"),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    setSaving(true);
    try {
      await LeadService.deleteCategory(categoryToDelete.id);
      setSnackbar({ open: true, message: "Category deleted", severity: "success" });
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      await loadCategories();
    } catch (err) {
      setSnackbar({
        open: true,
        message: apiErrorMessage(err, "Failed to delete category"),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const moveCategory = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= categories.length) return;

    const reordered = [...categories];
    const [item] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, item);

    setCategories(reordered);
    try {
      const updated = await LeadService.reorderCategories(reordered.map((c) => c.id));
      setCategories([...updated].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    } catch {
      setSnackbar({ open: true, message: "Failed to reorder categories", severity: "error" });
      await loadCategories();
    }
  };

  return (
    <Box sx={{ minHeight: "100%", bgcolor: PAGE_BG }}>
      <Paper
        elevation={0}
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: "1px solid #e4e7ef",
          borderRadius: 0,
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} color={PURPLE_DARK}>
              Lead categories
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {categories.length} label{categories.length === 1 ? "" : "s"} for classifying leads.{" "}
              <Link component={RouterLink} to="/leads" underline="hover" color={PURPLE}>
                Back to board
              </Link>
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            sx={{ bgcolor: PURPLE, fontWeight: 600, "&:hover": { bgcolor: PURPLE_DARK } }}
          >
            Add category
          </Button>
        </Box>
      </Paper>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: PURPLE }} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: "1px solid #e4e7ef", borderRadius: 2, overflow: "hidden" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f6f4fd" }}>
                  {["Order", "Category", "Color", "Move", ""].map((label) => (
                    <TableCell
                      key={label || "actions"}
                      align={label ? "left" : "right"}
                      sx={{ fontWeight: 700, color: PURPLE_DARK, fontSize: "0.8rem" }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category, index) => {
                  const color = getCategoryColor(category);
                  return (
                    <TableRow key={category.id} hover>
                      <TableCell sx={{ color: "text.secondary", width: 72 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={category.name}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: `${color}22`,
                            color,
                            border: `1px solid ${color}55`,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              bgcolor: color,
                              border: "1px solid #e4e7ef",
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {color}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: 100 }}>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              disabled={index === 0}
                              onClick={() => moveCategory(index, -1)}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              disabled={index === categories.length - 1}
                              onClick={() => moveCategory(index, 1)}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditDialog(category)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setCategoryToDelete(category);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {categories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No categories yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
          Categories label leads by source or type (e.g. Referral, Website). They are separate from
          pipeline stages. Delete a category only after no leads are assigned to it.
        </Alert>
      </Box>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: PURPLE_DARK }}>
          {editingCategory ? "Edit category" : "Add category"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Category name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            fullWidth
            autoFocus
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Color
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {LEAD_CATEGORY_COLORS.map((color) => (
                <Box
                  key={color}
                  onClick={() => setFormColor(color)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: color,
                    cursor: "pointer",
                    border: formColor === color ? `3px solid ${PURPLE_DARK}` : "2px solid #e4e7ef",
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCategory}
            disabled={saving || !formName.trim()}
            sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK } }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !saving && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete category?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete &quot;{categoryToDelete?.name}&quot;? Reassign leads using this category first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteCategory} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadCategoriesView;
